import { BellRing, ScrollText } from 'lucide-react';
import { api } from '../api/client';
import type { HeartbeatStatus, StatusPayload } from '../api/types';
import { ScreenLink } from '../components/ScreenLink';
import { ago, duration } from '../lib/format';
import { useLoad } from '../lib/useLoad';
import { LoadIssue } from '../components/LoadIssue';

/** sphere-watch's own vital signs. Who watches the watchmen, on a screen. */
export function Systems() {
  const { data, error, loading, updatedAt, reload } = useLoad(() => api.get<StatusPayload>('/status'), [], 10_000);

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (!data) return <LoadIssue error={error} updatedAt={null} what="sphere-watch's status" onRetry={() => void reload()} />;

  const scheduler = data.scheduler;
  // The single most important number on this screen. If nothing has been
  // committed recently, the monitor is not observing anything -- and every other
  // panel is showing the last thing it happened to see.
  const commitAge = scheduler?.lastCommitAgeMs ?? null;
  const stalled = commitAge !== null && commitAge > 5 * 60_000;
  const logs = data.logs;

  return (
    <>
      <div className="wt-head">
        <h1>Systems</h1>
        <span className="sub">sphere-watch itself</span>
        <span className="wt-spacer" />
        <ScreenLink to="/systems/alerting" previewTab="alerting" className="btn btn-secondary">
          <BellRing size={14} /> Alerting
        </ScreenLink>
        <ScreenLink to="/systems/logs" previewTab="logs" className="btn btn-secondary">
          <ScrollText size={14} /> Logs
        </ScreenLink>
      </div>
      <LoadIssue error={error} updatedAt={updatedAt} what="sphere-watch's status" onRetry={() => void reload()} />

      {!scheduler?.leader && (
        <div className="wt-banner wt-banner-warn">
          This instance is not the leader — it serves this API but schedules nothing. That is normal
          with two instances running; it is not normal if there is only one.
        </div>
      )}
      {stalled && (
        <div className="wt-banner wt-banner-danger">
          Nothing has been recorded for {duration(commitAge)}. The monitor is up but not observing.
        </div>
      )}
      {logs && logs.errorsLastHour > 0 && (
        <div className="wt-banner wt-banner-danger">
          sphere-watch logged {logs.errorsLastHour} error(s) in the last hour.{' '}
          <ScreenLink to="/systems/logs" previewTab="logs" search="level=error" className="wt-linkish">
            Read them
          </ScreenLink>
        </div>
      )}

      <div className="wt-table-wrap">
        <table className="wt-table wt-kv">
          <tbody>
            <tr>
              <th>Environment</th>
              <td className="wt-mono">{data.env}</td>
            </tr>
            <tr>
              <th>Build</th>
              <td className="wt-mono">{data.commit ?? 'unknown'}</td>
            </tr>
            <tr>
              <th>Leader</th>
              <td className="wt-mono">{scheduler?.leader ? 'yes — this instance schedules' : 'no'}</td>
            </tr>
            <tr>
              <th>Checks</th>
              <td className="wt-mono">
                {scheduler?.enabled ?? 0} enabled of {scheduler?.checks ?? 0} · {scheduler?.inFlight ?? 0} in flight
              </td>
            </tr>
            <tr>
              <th>Last observation recorded</th>
              <td className="wt-mono">{commitAge === null ? 'nothing yet' : `${duration(commitAge)} ago`}</td>
            </tr>
            <tr>
              <th>Dead-man's switch</th>
              <td>
                <HeartbeatCell heartbeat={data.heartbeat} leader={Boolean(scheduler?.leader)} />
              </td>
            </tr>
            <tr>
              <th>Open incidents</th>
              <td className="wt-mono">{data.openIncidents}</td>
            </tr>
            <tr>
              <th>Schema contracts</th>
              <td className="wt-mono">
                {data.contracts
                  ? Object.entries(data.contracts)
                      .map(([db, state]) => `${db}: ${state}`)
                      .join('  ·  ')
                  : 'not verified'}
              </td>
            </tr>
            <tr>
              <th>Its own log</th>
              <td>
                {logs ? (
                  <>
                    <div className="wt-mono">
                      {logs.errorsLastHour} error(s) · {logs.warningsLastHour} warning(s) in the last hour —{' '}
                      {logs.errorsLastDay} · {logs.warningsLastDay} in 24h · kept {logs.retentionDays} days{' '}
                      <ScreenLink
                        to="/systems/logs"
                        previewTab="logs"
                        search={logs.errorsLastDay || logs.warningsLastDay ? 'level=warn' : undefined}
                        className="wt-linkish"
                      >
                        open
                      </ScreenLink>
                    </div>
                    {logs.lastError && (
                      <div className="wt-dim" style={{ fontSize: 12.5 }} title={logs.lastError.message}>
                        last error {ago(logs.lastError.at)}: {logs.lastError.message.split('\n')[0].slice(0, 160)}
                      </div>
                    )}
                    {(logs.pending > 100 || logs.dropped > 0 || logs.lastFailure) && (
                      <div style={{ fontSize: 12.5, color: 'var(--warn)' }}>
                        {logs.pending} line(s) waiting to be written
                        {logs.dropped ? `, ${logs.dropped} dropped` : ''}
                        {logs.lastFailure ? ` — ${logs.lastFailure}` : ''}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="wt-mono">
                    could not be read —{' '}
                    <ScreenLink to="/systems/logs" previewTab="logs" className="wt-linkish">
                      open what this process still holds
                    </ScreenLink>
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * What this instance last told the external switch (core/heartbeat.js) -- the
 * one alarm that still works when this box is gone, so "not configured" is
 * drawn as the gap it is rather than left blank.
 */
function HeartbeatCell({ heartbeat, leader }: { heartbeat: HeartbeatStatus | null; leader: boolean }) {
  if (!heartbeat) return <span className="wt-mono wt-dim">not reported by this server</span>;
  if (!heartbeat.configured) {
    return (
      <span className="wt-mono" style={{ color: 'var(--warn)' }}>
        not configured — set WATCH_HEARTBEAT_URL, or nothing outside this box notices it dying
      </span>
    );
  }
  const every = `every ${duration(heartbeat.intervalMs)} → ${heartbeat.target ?? 'the switch'}`;
  const pinged = heartbeat.lastPingAt ? ago(heartbeat.lastPingAt) : 'never';
  const reason = heartbeat.lastReason && (
    <div className="wt-dim" style={{ fontSize: 12.5 }} title={heartbeat.lastReason}>
      {heartbeat.lastReason.slice(0, 200)}
    </div>
  );
  switch (heartbeat.lastOutcome) {
    case 'healthy':
      // A reason beside a healthy ping is a give-up the switch was told about
      // once and that still waits on Alerting: the switch is not held down for
      // it, so this is where it stays visible.
      return (
        <>
          <span className="wt-mono">healthy · pinged {pinged} · {every}</span>
          {reason}
        </>
      );
    case 'unhealthy':
      return (
        <>
          <div className="wt-mono" style={{ color: 'var(--danger)' }}>
            told the switch sphere-watch is unhealthy · {pinged}
          </div>
          {reason}
        </>
      );
    case 'undelivered':
      return (
        <>
          <div className="wt-mono" style={{ color: 'var(--warn)' }}>
            could not reach the switch · tried {pinged} — it fires on its own if this outlasts its grace
          </div>
          {reason}
        </>
      );
    case 'waiting':
      return (
        <>
          <div className="wt-mono">waiting for this leader's first recorded run before pinging</div>
          {reason}
        </>
      );
    case 'unknown':
      return (
        <>
          <div className="wt-mono" style={{ color: 'var(--warn)' }}>
            could not read the outbox · holding the ping (last sent {pinged})
          </div>
          {reason}
        </>
      );
    default:
      return (
        <span className="wt-mono">
          {leader
            ? `no ping yet — the first goes out ${duration(heartbeat.intervalMs)} after taking the lock · ${every}`
            : 'this instance is not the leader; the leader pings'}
        </span>
      );
  }
}
